import React from "react";
import { Edit, Trash2, Users, Calendar } from "lucide-react";

interface GroupsTableProps {
  groups: any[];
  groupsLoading: boolean;
  groupsError: string | null;
  onEditGroup: (group: any) => void;
  onDeleteGroup: (group: any) => void;
}

const GroupsTable: React.FC<GroupsTableProps> = ({
  groups,
  groupsLoading,
  groupsError,
  onEditGroup,
  onDeleteGroup,
}) => {
  const filteredGroups = Array.isArray(groups) ? groups : [];

  if (filteredGroups.length === 0) {
    return (
      <div className="text-center py-12">
        <Users className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          No groups found
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Get started by creating your first group.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Group Details
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Recipients
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Created
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {filteredGroups.map((group) => (
            <tr key={group._id} className="hover:bg-gray-50">
              <td className="px-6 py-4 whitespace-nowrap">
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {group.name}
                  </div>
                  {group.description && (
                    <div className="text-sm text-gray-500 mt-1">
                      {group.description}
                    </div>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  <Users className="h-4 w-4 text-gray-400 mr-2" />
                  <span className="text-sm text-gray-900">
                    {group.recipients?.length || 0} recipients
                  </span>
                </div>
                {group.recipients && group.recipients.length > 0 && (
                  <div className="text-xs text-gray-500 mt-1 max-w-xs truncate">
                    {group.recipients
                      .slice(0, 2)
                      .map((r: any) => r.name || r.email)
                      .join(", ")}
                    {group.recipients.length > 2 && "..."}
                  </div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center text-sm text-gray-500">
                  <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                  {new Date(group.createdAt).toLocaleDateString()}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => onEditGroup(group)}
                    className="text-blue-600 hover:text-blue-900 p-1 rounded"
                    title="Edit group"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onDeleteGroup(group)}
                    className="text-red-600 hover:text-red-900 p-1 rounded"
                    title="Delete group"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default GroupsTable;
